import { PDFDocument, PDFHexString, type PDFPage } from 'pdf-lib';
import {
  highlightColorRgb,
  type FileHighlight,
  type HighlightRect,
} from '@/utils/fileHighlights';

function addHighlightAnnotation(
  pdf: PDFDocument,
  page: PDFPage,
  rects: HighlightRect[],
  highlight: FileHighlight,
  authorName: string,
) {
  if (rects.length === 0) return;
  const { width, height } = page.getSize();
  const color = highlightColorRgb(highlight.color);
  const quads: number[] = [];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const rect of rects) {
    const x1 = rect.x * width;
    const x2 = (rect.x + rect.w) * width;
    const yTop = (1 - rect.y) * height;
    const yBottom = (1 - (rect.y + rect.h)) * height;
    quads.push(x1, yTop, x2, yTop, x1, yBottom, x2, yBottom);
    minX = Math.min(minX, x1);
    maxX = Math.max(maxX, x2);
    minY = Math.min(minY, yBottom);
    maxY = Math.max(maxY, yTop);
  }

  const contents = [highlight.comment, highlight.quote].filter(Boolean).join('\n\n') || highlight.quote;
  const annot = pdf.context.obj({
    Type: 'Annot',
    Subtype: 'Highlight',
    Rect: [minX, minY, maxX, maxY],
    QuadPoints: quads,
    C: [color.r, color.g, color.b],
    /* Inks are already light. A higher opacity keeps a multiply viewer close
       to the on-screen wash without painting a solid gold bar. */
    CA: 0.55,
    Contents: PDFHexString.fromText(contents),
    T: PDFHexString.fromText(authorName),
    P: page.ref,
  });
  page.node.addAnnot(pdf.context.register(annot));
}

export async function embedHighlightsInPdfCopy(input: {
  pdfBytes: ArrayBuffer | Uint8Array;
  highlights: FileHighlight[];
  authorNameByUserId: Record<string, string>;
}): Promise<Uint8Array> {
  const pdf = await PDFDocument.load(input.pdfBytes);
  const pages = pdf.getPages();

  for (const highlight of input.highlights) {
    const page = pages[highlight.page - 1];
    if (!page) continue;
    const author =
      highlight.author_name ||
      input.authorNameByUserId[highlight.created_by] ||
      highlight.created_by;
    addHighlightAnnotation(pdf, page, highlight.rects, highlight, author);
  }

  return pdf.save();
}

export function markedDownloadFilename(filename: string): string {
  const trimmed = filename.trim() || 'document.pdf';
  if (/\.pdf$/i.test(trimmed)) {
    return trimmed.replace(/\.pdf$/i, '-markeringer.pdf');
  }
  return `${trimmed}.markeringer.pdf`;
}
